import { openOrderSplitModalForSelectedOrder } from '@/renderer/store/runningOrder/runningOrderThunks';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  confirmKotForSelectedOrder,
  openBillForSelectedOrder,
  openDiscountForSelectedOrder,
  openKotNumberModal,
  openOrderShiftModalForSelectedOrder,
  openSettleModalForSelectedOrder,
  openWaiterModalForSelectedOrder,
  selectCanOfferDiscounts,
  selectCanPrintBills,
  selectCanShiftOrders,
  selectIsBillActionDisabled,
  selectIsConfirmingKot,
  selectIsDiscountActionDisabled,
  selectIsKotActionDisabled,
  selectIsKotTrackingRequiredForOutlet,
  selectIsLoadingSettleOptions,
  selectIsLoadingWaiters,
  selectIsOrderShiftActionDisabled,
  selectIsPreparingBill,
  selectIsPreparingDiscount,
  selectIsPreparingOrderShift,
  selectIsRestaurantBarOutlet,
  selectIsSettleActionDisabled,
  selectIsShiftingOrder,
  selectIsWaiterActionDisabled,
} from '@store/runningOrder';
import {
  ArrowLeftRight,
  CreditCard,
  Loader2,
  Percent,
  Receipt,
  SplitIcon,
  User as UserIcon,
  UtensilsCrossed,
} from 'lucide-react';
import React from 'react';
import { ONBOARDING_TOUR_TARGETS } from '../../services/onboardingTourService';

export const OrderActionButtons: React.FC = () => {
  const dispatch = useAppDispatch();
  const isDiscountDisabled = useAppSelector(selectIsDiscountActionDisabled);
  const isBillDisabled = useAppSelector(selectIsBillActionDisabled);
  const isSettleDisabled = useAppSelector(selectIsSettleActionDisabled);
  const isKotDisabled = useAppSelector(selectIsKotActionDisabled);
  const isWaiterDisabled = useAppSelector(selectIsWaiterActionDisabled);
  const isOrderShiftDisabled = useAppSelector(selectIsOrderShiftActionDisabled);
  const canOfferDiscounts = useAppSelector(selectCanOfferDiscounts);
  const canPrintBills = useAppSelector(selectCanPrintBills);
  const canShiftOrders = useAppSelector(selectCanShiftOrders);
  const isPreparingDiscount = useAppSelector(selectIsPreparingDiscount);
  const isPreparingBill = useAppSelector(selectIsPreparingBill);
  const isLoadingSettleOptions = useAppSelector(selectIsLoadingSettleOptions);
  const isConfirmingKot = useAppSelector(selectIsConfirmingKot);
  const isLoadingWaiters = useAppSelector(selectIsLoadingWaiters);
  const isPreparingOrderShift = useAppSelector(selectIsPreparingOrderShift);
  const isShiftingOrder = useAppSelector(selectIsShiftingOrder);
  const isRestaurantBarOutlet = useAppSelector(selectIsRestaurantBarOutlet);
  const isKotTrackingRequired = useAppSelector(
    selectIsKotTrackingRequiredForOutlet,
  );

  const actions = [
    {
      id: 'waiter',
      label: 'WAITER',
      icon: <UserIcon size={20} />,
      loading: isLoadingWaiters,
      onClick: () => {
        dispatch(openWaiterModalForSelectedOrder());
      },
      disabled: isWaiterDisabled,
    },
    {
      id: 'kot',
      label: 'KOT',
      icon: <UtensilsCrossed size={20} />,
      loading: isConfirmingKot,
      onClick: () => {
        if (isKotTrackingRequired) {
          dispatch(openKotNumberModal());
        } else {
          dispatch(confirmKotForSelectedOrder(undefined));
        }
      },
      disabled: isKotDisabled,
    },
    ...(isRestaurantBarOutlet && canShiftOrders
      ? [
          {
            id: 'shift',
            label: 'SHIFT',
            icon: <ArrowLeftRight size={20} />,
            loading: isPreparingOrderShift || isShiftingOrder,
            onClick: () => {
              dispatch(openOrderShiftModalForSelectedOrder());
            },
            disabled: isOrderShiftDisabled,
          },
          {
            id: 'split',
            label: 'SPLIT',
            icon: <SplitIcon size={20} />,
            loading: isPreparingOrderShift || isShiftingOrder,
            onClick: () => {
              dispatch(openOrderSplitModalForSelectedOrder());
            },
            disabled: isOrderShiftDisabled || isSettleDisabled,
          },
        ]
      : []),
    ...(canPrintBills
      ? [
          {
            id: 'bill',
            label: 'BILL',
            icon: <Receipt size={20} />,
            loading: isPreparingBill,
            onClick: () => {
              dispatch(openBillForSelectedOrder());
            },
            disabled: isBillDisabled,
          },
        ]
      : []),
    {
      id: 'settle',
      label: 'SETTLE',
      icon: <CreditCard size={20} />,
      loading: isLoadingSettleOptions,
      onClick: () => {
        dispatch(openSettleModalForSelectedOrder());
      },
      disabled: isSettleDisabled,
    },
    ...(canOfferDiscounts
      ? [
          {
            id: 'discount',
            label: 'DISCOUNT',
            icon: <Percent size={20} />,
            loading: isPreparingDiscount,
            onClick: () => {
              dispatch(openDiscountForSelectedOrder());
            },
            disabled: isDiscountDisabled,
          },
        ]
      : []),
  ];

  return (
    <div
      className="flex h-full flex-col gap-2"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.salesActionPanel}
    >
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          data-onboarding-tour={
            action.id === 'waiter'
              ? ONBOARDING_TOUR_TARGETS.salesActionWaiter
              : action.id === 'kot'
                ? ONBOARDING_TOUR_TARGETS.salesActionKOT
                : action.id === 'bill'
                  ? ONBOARDING_TOUR_TARGETS.salesActionBill
                  : action.id === 'settle'
                    ? ONBOARDING_TOUR_TARGETS.salesActionSettle
                    : action.id === 'discount'
                      ? ONBOARDING_TOUR_TARGETS.salesActionDiscount
                      : undefined
          }
          className="app-hover-bg-dark flex h-16 w-full flex-col items-center justify-center gap-1 rounded-md border border-gray-200 bg-white p-2 text-black transition-colors hover:border-gray-300 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
          onClick={action.onClick}
          disabled={action.disabled}
          title={action.label}
        >
          {action.loading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            action.icon
          )}
          <span className="text-sm font-semibold uppercase tracking-wide">
            {action.loading ? 'Checking' : action.label}
          </span>
        </button>
      ))}
    </div>
  );
};
